import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import heroLandscapeImage from '../assets/images/hero-landscape.png';
import { Course } from '../types/course';
import { BASE_URL } from '../apiConfig';
import { BRAND_NAME } from '../config/brand';
import { getTenantHeaders } from '../config/tenant';
import axios from 'axios';

// Import UnitCodePopup
import UnitCodePopup from '../components/common/UnitCodePopup';
import HomeSectionCard from '../components/home/HomeSectionCard';
import WhyChooseBanner from '../components/home/WhyChooseBanner';
import SuggestedCoursesCarousel from '../components/home/SuggestedCoursesCarousel';
import TeachersSection from '../components/home/TeachersSection';
import HomeCtaBanner from '../components/home/HomeCtaBanner';
import HomeFaq from '../components/home/HomeFaq';
import Students2027RegisterPopup from '../components/home/Students2027RegisterPopup';
import SupportTelegramFab from '../components/common/SupportTelegramFab';
import { BookOpen, Sparkles } from 'lucide-react';

// Simple decorative elements (minimal)
const SimpleDecorations = React.memo(() => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-5">
    {/* Simple geometric shapes */}
    <div className="absolute top-20 left-20 w-16 h-16 bg-green-500 rounded-full"></div>
    <div className="absolute top-40 right-40 w-12 h-12 bg-gold-500 rounded-full"></div>
    <div className="absolute bottom-40 left-40 w-20 h-20 bg-green-600 rounded-full"></div>
  </div>
));

SimpleDecorations.displayName = 'SimpleDecorations';

const Home: React.FC = () => {
  const { isAuthenticated, currentUser, hasEnrolledCourses, checkEnrolledCourses } = useAuth();
  const navigate = useNavigate();
  const [isUnitCodePopupOpen, setIsUnitCodePopupOpen] = useState(false);
  const isCheckingEnrollment = isAuthenticated && hasEnrolledCourses === null;
  const [recommendedCourses, setRecommendedCourses] = useState<Course[]>([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  
  // Fix hydration mismatch: only render auth-dependent content after mount
  const [mounted, setMounted] = useState(false);
  
  useEffect(() => {
    setMounted(true);
  }, []);

  // Trigger enrolled-courses check when authenticated (uses AuthContext cache; redirect handled by App NavigationHandler)
  useEffect(() => {
    if (isAuthenticated && currentUser?.id && hasEnrolledCourses === null) {
      checkEnrolledCourses();
    }
  }, [isAuthenticated, currentUser?.id, hasEnrolledCourses, checkEnrolledCourses]);

  // Fetch recommended courses
  useEffect(() => {
    const fetchRecommendedCourses = async () => {
      try {
        setLoadingCourses(true);
        const response = await axios.get<{
          success: boolean;
          message?: string;
          data?: {
            currentPage: number;
            pageSize: number;
            totalPages: number;
            totalCount: number;
            hasPreviousPage: boolean;
            hasNextPage: boolean;
            data: Course[];
          };
        }>(`${BASE_URL}api/Course/suggested`, {
          params: {
            pageNumber: 1,
            pageSize: 6
          },
          headers: {
            ...getTenantHeaders()
          }
        });

        if (response.data?.success && response.data?.data?.data) {
          setRecommendedCourses(response.data.data.data);
        }
      } catch (error) {
        console.error('Error fetching recommended courses:', error);
      } finally {
        setLoadingCourses(false);
      }
    };

    fetchRecommendedCourses();
  }, []);

  // Show loading state while checking enrollment
  if (isCheckingEnrollment) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-b from-green-50/50 via-white to-gold-50/30" dir="rtl">

      {/* 1. Hero Section — extends under fixed navbar */}
      <section className="relative">
        <div className="relative h-[78vh] sm:h-[85vh] lg:h-[92vh] min-h-[440px] lg:min-h-[640px] overflow-hidden">
          <img
            src={heroLandscapeImage}
            alt={`طالب أزهري يذاكر اللغة العربية على منصة ${BRAND_NAME} مع الأستاذ سعيد الفقي - منصة تعليمية أونلاين للثانوية الأزهرية`}
            title="منصة نهج الأزهر - تعليم أزهري أونلاين"
            className="absolute inset-0 w-full h-full object-cover object-[25%_40%] sm:object-[22%_center]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/15 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
          <div className="absolute inset-0 flex flex-col items-center justify-end pb-10 sm:pb-14 px-5 sm:px-6 text-center pt-16 sm:pt-[4.5rem]">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3 font-cairo-bold drop-shadow-md">
              {BRAND_NAME}
            </h1>
            <p className="text-base sm:text-lg text-white/90 leading-relaxed max-w-2xl drop-shadow-sm">
              منصة تعليمية لطلاب الثانوية الأزهرية — دروس وشروحات بجودة عالية
            </p>
          </div>
        </div>

        <div className="bg-white px-5 sm:px-6 py-6 sm:py-8">
          <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-3">
            {mounted && !isAuthenticated && (
              <button
                onClick={() => navigate('/register')}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg px-8 py-3 text-base font-semibold text-white bg-green-800 hover:bg-green-900 transition-colors"
              >
                ابدأ الآن
              </button>
            )}
            <button
              onClick={() => document.getElementById('suggested-courses-section')?.scrollIntoView({ behavior: 'smooth' })}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg px-8 py-3 text-base font-semibold text-green-800 border border-green-800 hover:bg-green-50 transition-colors"
            >
              <BookOpen className="w-4 h-4" />
              الكورسات
            </button>
          </div>
        </div>
      </section>

      <WhyChooseBanner />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 space-y-6 sm:space-y-8">
      <HomeSectionCard id="suggested-courses-section" innerClassName="p-0 sm:p-0">
        <div className="overflow-x-hidden p-6 sm:p-8 lg:p-10">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between lg:gap-10">
            <div className="w-full shrink-0 text-center lg:w-[20%] xl:w-[18%] lg:text-right lg:pe-3">
              <span className="inline-flex items-center gap-2 bg-green-50 text-green-700 border border-green-100 px-3 py-1 rounded-full text-xs sm:text-sm font-semibold mb-4">
                <Sparkles className="w-4 h-4 text-gold-600" />
                مختاراتنا ليك
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-green-800 mb-3 sm:mb-4 leading-tight font-cairo-bold">
                كورسات هتفرق معاك
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-md lg:max-w-none lg:ms-auto leading-relaxed">
                المنصة مجمعة لك أهم الكورسات والحصص هنا
              </p>
            </div>
            <div className="w-full min-w-0 overflow-x-hidden lg:flex-1 lg:max-w-[85%] xl:max-w-[88%]">
              <SuggestedCoursesCarousel
                courses={recommendedCourses}
                loading={loadingCourses}
                compact
              />
            </div>
          </div>
        </div>
      </HomeSectionCard>

      <HomeCtaBanner />

      <HomeSectionCard id="teachers-section" className="relative z-10" innerClassName="p-0 sm:p-0">
        <div className="overflow-x-hidden p-6 sm:p-8 lg:p-10">
          <TeachersSection embedded />
        </div>
      </HomeSectionCard>
      </div>

      <HomeFaq />

      <SupportTelegramFab />

      {/* Unit Code Popup */}
      <UnitCodePopup
        isOpen={isUnitCodePopupOpen}
        onClose={() => setIsUnitCodePopupOpen(false)}
      />

      {mounted && !isAuthenticated && <Students2027RegisterPopup enabled />}

    </div>
  );
};

export default React.memo(Home);
